import React, { useEffect, useState, useRef } from 'react';
import { motion, useInView, useMotionValue, useTransform, animate } from 'framer-motion';
import { useLanguage } from './LanguageContext';
const AnimatedNumber = ({
  value
}: {
  value: number;
}) => {
  const ref = useRef(null);
  const isInView = useInView(ref, {
    once: true,
    margin: '-50px'
  });
  const count = useMotionValue(0);
  const rounded = useTransform(count, latest => {
    // If it's a decimal like 4.9, keep 1 decimal place, otherwise round to whole number
    return value % 1 !== 0 ? latest.toFixed(1) : Math.round(latest);
  });
  useEffect(() => {
    if (isInView) {
      animate(count, value, {
        duration: 2,
        ease: 'easeOut'
      });
    }
  }, [isInView, value, count]);
  return <motion.span ref={ref} data-id="element-832">{rounded}</motion.span>;
};
export const StatsSection = () => {
  const {
    t
  } = useLanguage();
  return <section className="py-24 bg-brand-black relative overflow-hidden border-t border-white/5" data-id="element-833">
      {/* Background photo */}
      <div className="absolute inset-0 z-0" data-id="element-834">
        <img src="https://cdn.magicpatterns.com/uploads/pGnNzPy97nQ4T7bdykcyeG/hf_20260430_172931_b5d5b9be-937a-4409-b990-da37a4f92f81.png" alt="" className="w-full h-full object-cover" data-id="element-835" />
        <div className="absolute inset-0 bg-brand-black/85" data-id="element-836" />
      </div>

      {/* Background dot grid */}
      <div className="absolute inset-0 opacity-20 pointer-events-none z-[1]" style={{
      backgroundImage: 'radial-gradient(circle at 2px 2px, rgba(255,255,255,0.15) 1px, transparent 0)',
      backgroundSize: '24px 24px'
    }} data-id="element-837" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10" data-id="element-838">
        <div className="text-center mb-16" data-id="element-839">
          <div className="h-1 w-24 bg-brand-red mx-auto mb-8" data-id="element-840" />
          <h2 className="font-oswald text-4xl md:text-5xl font-bold text-white mb-4 uppercase" data-id="element-841">
            {t.stats.title}
          </h2>
          <p className="text-xl text-gray-400 max-w-2xl mx-auto" data-id="element-842">
            {t.stats.subtitle}
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center" data-id="element-843">
          {/* Left: Counter Grid */}
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 sm:gap-6" data-id="element-844">
            {t.stats.items.map((item: any, index: number) => <motion.div key={index} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true,
            margin: '-50px'
          }} transition={{
            duration: 0.5,
            delay: index * 0.1
          }} className="bg-white/5 border border-white/10 rounded-xl p-6 text-center hover:border-brand-red/30 transition-colors" data-id="element-845">
                <div className="font-oswald text-4xl sm:text-5xl font-bold text-white mb-2 flex items-center justify-center" data-id="element-846">
                  <AnimatedNumber value={item.value} data-id="element-847" />
                  <span className="text-brand-red ml-1" data-id="element-848">{item.suffix}</span>
                </div>
                <h4 className="text-sm font-bold text-white uppercase tracking-wider mb-1" data-id="element-849">
                  {item.label}
                </h4>
                <p className="text-xs text-gray-500" data-id="element-850">{item.desc}</p>
              </motion.div>)}
          </div>

          {/* Right: Progress Bars */}
          <div className="space-y-8" data-id="element-851">
            {t.stats.bars.map((bar: any, index: number) => <div key={index} className="space-y-2" data-id="element-852">
                <div className="flex justify-between items-end" data-id="element-853">
                  <span className="text-sm font-bold text-white uppercase tracking-wider" data-id="element-854">
                    {bar.label}
                  </span>
                  <span className="font-oswald text-xl font-bold text-brand-yellow" data-id="element-855">
                    <AnimatedNumber value={bar.value} data-id="element-856" />%
                  </span>
                </div>
                <div className="h-3 bg-white/10 rounded-full overflow-hidden relative" data-id="element-857">
                  <motion.div initial={{
                width: 0
              }} whileInView={{
                width: `${bar.value}%`
              }} viewport={{
                once: true,
                margin: '-50px'
              }} transition={{
                duration: 1.5,
                delay: 0.5 + index * 0.2,
                ease: 'easeOut'
              }} className="absolute top-0 left-0 h-full bg-brand-red rounded-full relative overflow-hidden" data-id="element-858">
                    {/* Shimmer effect */}
                    <motion.div animate={{
                  x: ['-100%', '200%']
                }} transition={{
                  repeat: Infinity,
                  duration: 2,
                  ease: 'linear'
                }} className="absolute top-0 left-0 w-1/2 h-full bg-gradient-to-r from-transparent via-white/30 to-transparent skew-x-12" data-id="element-859" />
                  </motion.div>
                </div>
              </div>)}
          </div>
        </div>
      </div>
    </section>;
};